import { useTranslation } from "react-i18next"
import { Grid, Stack, Typography } from "@mui/material"
import { isValidElement, useMemo } from "react"
import type { ReactNode } from "react"

import PlotHelpTooltip from "widgets/plots/PlotHelpTooltip"

interface LabelValueStackProps {
  labelKey: string
  value: ReactNode
  orientation?: "horizontal" | "vertical"
  tooltipContent?: ReactNode[]
}

const LabelValueStack = ({
  labelKey,
  value,
  orientation = "horizontal",
  tooltipContent,
}: LabelValueStackProps) => {
  const { t } = useTranslation()

  const alignment = useMemo(
    () => (orientation === "horizontal" ? "center" : "start"),
    [orientation],
  )
  const size = useMemo(() => (orientation === "horizontal" ? 6 : 12), [orientation])
  const columnSpacing = useMemo(
    () => (orientation === "horizontal" ? 4 : 0),
    [orientation],
  )
  const rowSpacing = useMemo(() => (orientation === "vertical" ? 2 : 0), [orientation])

  return (
    <Grid
      container
      columnSpacing={columnSpacing}
      rowSpacing={rowSpacing}
      mb={1}
      alignItems={alignment}
    >
      <Grid item xs={size}>
        <Stack flexDirection="row" alignItems="center" columnGap={0.5}>
          <Typography>{t(labelKey)}</Typography>
          {tooltipContent && <PlotHelpTooltip content={tooltipContent} />}
        </Stack>
      </Grid>
      <Grid item xs={size}>
        {/* Text gets the muted value style, a node brings its own. */}
        {isValidElement(value) ? (
          value
        ) : (
          <Typography color={"text.disabled"}>{value ?? "-"}</Typography>
        )}
      </Grid>
    </Grid>
  )
}

export default LabelValueStack
